import { searchMemoryAction } from '@/app/actions/memory/search';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useAnalytics } from '@/hooks/use-analytics';
import { useReasoning } from '@/hooks/use-reasoning';
import { handleError } from '@/lib/error/handle';
import { textModels } from '@/lib/models/text';
import {
  getDescriptionsFromImageNodes,
  getFilesFromFileNodes,
  getImagesFromImageNodes,
  getMemoryNodeIds,
  getTextFromTextNodes,
  getTranscriptionFromAudioNodes,
  getTweetContentFromTweetNodes,
  hasMemoryNodes,
} from '@/lib/xyflow';
import { useProject } from '@/providers/project';
import { ReasoningTunnel } from '@/tunnels/reasoning';
import { useChat } from '@ai-sdk/react';
import { getIncomers, useReactFlow } from '@xyflow/react';
import { ClockIcon, PlayIcon, RotateCcwIcon, SquareIcon } from 'lucide-react';
import {
  type ChangeEventHandler,
  type ComponentProps,
  useCallback,
  useMemo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { LLMNodeProps } from '.';
import { NodeLayout } from '../layout';
import { ModelSelector } from '../model-selector';

type LLMTransformProps = LLMNodeProps & {
  title: string;
};

export const LLMTransform = ({ data, id, type, title }: LLMTransformProps) => {
  const { updateNodeData, getNodes, getEdges } = useReactFlow();
  const project = useProject();
  const analytics = useAnalytics();
  const [reasoning, setReasoning] = useReasoning();

  // Get the default model if none is set
  const getDefaultModel = (models: typeof textModels) => {
    const defaultModel = models
      .flatMap((model) => model.models)
      .find((model) => model.default);

    if (!defaultModel) {
      return models[0]?.models[0]?.id || '';
    }

    return defaultModel.id;
  };

  const modelId = data.model ?? getDefaultModel(textModels);

  const { append, messages, setMessages, status, stop } = useChat({
    body: {
      modelId,
      systemPrompt: data.systemPrompt,
    },
    onError: (error) => handleError('Error generating LLM response', error),
    onFinish: (message) => {
      updateNodeData(id, {
        generated: {
          text: message.content,
        },
        text: message.content, // Set the output text for next nodes
        updatedAt: new Date().toISOString(),
      });

      setReasoning((oldReasoning) => ({
        ...oldReasoning,
        isGenerating: false,
      }));

      toast.success('LLM response generated successfully');
      setTimeout(() => mutate('credits'), 5000);
    },
  });

  const handleGenerate = useCallback(async () => {
    const incomers = getIncomers({ id }, getNodes(), getEdges());
    const textPrompts = getTextFromTextNodes(incomers);
    const audioPrompts = getTranscriptionFromAudioNodes(incomers);
    const images = getImagesFromImageNodes(incomers);
    const imageDescriptions = getDescriptionsFromImageNodes(incomers);
    const tweetContent = getTweetContentFromTweetNodes(incomers);
    const files = getFilesFromFileNodes(incomers);

    // Check for any valid input sources
    const hasValidInput =
      textPrompts.length > 0 ||
      audioPrompts.length > 0 ||
      hasMemoryNodes(incomers) ||
      data.systemPrompt;

    if (!hasValidInput) {
      handleError(
        'Error generating LLM response',
        'No input found from connected nodes or system prompt'
      );
      return;
    }

    const content: string[] = [];

    if (data.systemPrompt) {
      content.push('--- System Instructions ---', data.systemPrompt);
    }

    // Handle memory nodes - both search and provide IDs
    if (hasMemoryNodes(incomers)) {
      const memoryIds = getMemoryNodeIds(incomers);

      if (memoryIds.length > 0) {
        console.log(
          '🧠 Memory nodes detected, using memory IDs and searching content:',
          memoryIds
        );

        // Add memory IDs as reference
        const memoryIdText = `Memory IDs: ${memoryIds.join(', ')}`;
        content.push('--- Memory References ---', memoryIdText);
      }

      const { memories, error, errorMessage } = await searchMemoryAction({
        query: content.join('\n'),
        projectId: project?.id,
        limit: 5,
      });

      if (error) {
        handleError('Error searching memories', errorMessage);
        return;
      }

      if (memories.length > 0) {
        content.push(
          '--- Memory Content ---',
          memories
            .map((memory) =>
              memory.chunks
                .map((chunk) => chunk.content)
                .join('\n\n')
                .replace(/^/gm, '  ')
            )
            .join('\n')
        );
      }

      console.log('🔍 Search result:', memories);
    }

    if (textPrompts.length) {
      content.push('--- User Input ---', ...textPrompts);
    }

    if (audioPrompts.length) {
      content.push('--- Audio Transcriptions ---', ...audioPrompts);
    }

    if (imageDescriptions.length) {
      content.push('--- Image Descriptions ---', ...imageDescriptions);
    }

    if (tweetContent.length) {
      content.push('--- Tweet Content ---', ...tweetContent);
    }

    analytics.track('canvas', 'node', 'generate', {
      type,
      promptLength: content.join('\n').length,
      model: modelId,
      systemPromptLength: data.systemPrompt?.length ?? 0,
      imageCount: images.length,
      fileCount: files.length,
    });

    setMessages([]);
    append({
      role: 'user',
      content: content.join('\n'),
      experimental_attachments: [
        ...images.map((image) => ({
          url: image.url,
          contentType: image.type,
        })),
        ...files.map((file) => ({
          url: file.url,
          contentType: file.type,
          name: file.name,
        })),
      ],
    });
  }, [
    append,
    data.systemPrompt,
    getEdges,
    getNodes,
    id,
    modelId,
    type,
    analytics.track,
    setMessages,
    project?.id,
  ]);

  const handleSystemPromptChange: ChangeEventHandler<HTMLTextAreaElement> = (
    event
  ) => updateNodeData(id, { systemPrompt: event.target.value });

  const toolbar = useMemo(() => {
    const items: ComponentProps<typeof NodeLayout>['toolbar'] = [];

    items.push({
      children: (
        <ModelSelector
          value={modelId}
          options={textModels}
          key={id}
          className="w-[200px] rounded-full"
          onChange={(value) => updateNodeData(id, { model: value })}
        />
      ),
    });

    if (status === 'submitted' || status === 'streaming') {
      items.push({
        tooltip: 'Stop',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            variant="llm"
            onClick={stop}
            disabled={!project?.id}
          >
            <SquareIcon size={12} />
          </Button>
        ),
      });
    } else if (messages.length || data.generated?.text) {
      items.push({
        tooltip: 'Regenerate',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            variant="llm"
            onClick={handleGenerate}
            disabled={!project?.id}
          >
            <RotateCcwIcon size={12} />
          </Button>
        ),
      });
    } else {
      items.push({
        tooltip: 'Generate',
        children: (
          <Button
            size="icon"
            className="rounded-full"
            variant="llm"
            onClick={handleGenerate}
            disabled={!project?.id}
          >
            <PlayIcon size={12} />
          </Button>
        ),
      });
    }

    if (data.updatedAt) {
      items.push({
        tooltip: `Last updated: ${new Intl.DateTimeFormat('en-US', {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(new Date(data.updatedAt))}`,
        children: (
          <Button size="icon" variant="ghost" className="rounded-full">
            <ClockIcon size={12} />
          </Button>
        ),
      });
    }

    return items;
  }, [
    data.generated?.text,
    data.updatedAt,
    handleGenerate,
    updateNodeData,
    modelId,
    id,
    messages,
    project?.id,
    status,
    stop,
  ]);

  const isGenerating = status === 'submitted' || status === 'streaming';
  const lastMessage = messages
    .filter((message) => message.role === 'assistant')
    .at(-1);

  const nonUserMessages = messages.filter((message) => message.role !== 'user');

  return (
    <>
      <ReasoningTunnel.In>
        <div className="flex items-center gap-2 p-4">
          <ClockIcon size={12} />
          <p className="text-xs">
            {reasoning.isGenerating
              ? 'Generating LLM response...'
              : 'Ready to generate'}
          </p>
        </div>
      </ReasoningTunnel.In>
      <NodeLayout
        id={id}
        data={data}
        title={title}
        type={type}
        toolbar={toolbar}
        className="node-llm w-96"
      >
        <div className="nowheel flex size-full h-full max-h-[30rem] flex-col divide-y overflow-auto">
          <div className="flex flex-col gap-2 p-3">
            <label
              htmlFor={`system-prompt-${id}`}
              className="font-medium text-muted-foreground text-xs"
            >
              System Prompt
            </label>
            <Textarea
              id={`system-prompt-${id}`}
              placeholder="e.g., You are a helpful assistant."
              value={data.systemPrompt || ''}
              onChange={handleSystemPromptChange}
              className="min-h-20 text-sm"
              disabled={isGenerating}
            />
          </div>

          <div className="flex-1 p-3">
            {isGenerating && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-3/5" />
              </div>
            )}

            {lastMessage && !isGenerating && (
              <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                <ReactMarkdown>{lastMessage.content}</ReactMarkdown>
              </div>
            )}

            {data.generated?.text && !lastMessage && !isGenerating && (
              <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                <ReactMarkdown>{data.generated.text}</ReactMarkdown>
              </div>
            )}

            {!data.generated?.text &&
              !nonUserMessages.length &&
              status !== 'submitted' && (
                <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-secondary">
                  <p className="text-muted-foreground text-sm">
                    Press{' '}
                    <PlayIcon size={12} className="-translate-y-px inline" /> to
                    generate text
                  </p>
                </div>
              )}
          </div>
        </div>
      </NodeLayout>
    </>
  );
};
